import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  globalStyle,
  handleVisible,
  handleDangerVisible,
} from "../../global/Utils";
import { getProvince } from "../../Actions/CompanyAction";
import { getUserList } from "../../Actions/UserAction";
import { InputField, MobileNumberField } from "../Common/InputField";
import { CircularProgress } from "@mui/material";
import {
  addBuyer,
  checkBuyerTaxStatus,
  clearBuyerTaxStatus,
  getBuyerTaxStatus,
} from "../../Actions/BuyerAction";

import { upperCaseInput } from "../../global/textCase";
import { completeTaxId, formatTaxId } from "../../global/taxId";
import { SearchableSelect } from "../Common/SearchableSelect";
import { TaxStatusBox } from "./TaxStatusBox";

// "With CNIC": the buyer is looked up by CNIC - tax status, and the business
// name from the Active Taxpayer List - and saved as Registered.
// "Without CNIC": everything is entered by hand; an NTN is optional, and
// decides whether the buyer is saved as Registered or Unregistered.
const TAB = { CNIC: "cnic", MANUAL: "manual" };
const TABS = [
  { value: TAB.CNIC, label: "With CNIC" },
  { value: TAB.MANUAL, label: "Without CNIC" },
];
// With CNIC: the ATL Status dropdown shows what the Active Taxpayer List says
// for the number (Active until it answers), can be changed by hand, and is
// saved as picked.
const ATL_STATUSES = ["Active", "Inactive"];
const DEFAULT_ATL_STATUS = "Active";

// Edit Buyer opens this same form with the buyer it edits (see EditBuyer.jsx),
// filled in as it was saved: on With CNIC when it has a number (looked up by
// CNIC or NTN, by its length), on Without CNIC when it has none. The statuses
// saved for its number stand until the number is changed; only one it never
// got (or got as Unverified) is checked again.
const savedNumberOf = (buyer) =>
  String(buyer?.registrationNo ?? "")
    .trim()
    .toUpperCase();
const savedContactOf = (contact) => {
  const digits = String(contact ?? "")
    .replace(/\D/g, "")
    .replace(/^92/, "")
    .slice(0, 10);
  return digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
};
const savedCheck = (status) => ({
  isChecking: false,
  status,
  error: "",
  taxpayer: null,
});

const AddBuyer = ({ buyer, onSave, onCancel }) => {
  const dispatch = useDispatch();
  const formRef = React.useRef(null);
  const isEdit = Boolean(buyer);
  const savedNumber = savedNumberOf(buyer);
  const savedSalesTax =
    isEdit && buyer.salesTaxStatus && buyer.salesTaxStatus !== "Unverified"
      ? buyer.salesTaxStatus
      : "";
  const savedAtl =
    isEdit && ATL_STATUSES.includes(buyer.atlStatus) ? buyer.atlStatus : "";
  const savedBusinessName = buyer?.businessName ?? "";

  const isAddingBuyer = useSelector(
    (state) => state.BuyerReducer?.isAddingBuyer,
  );
  const isUpdatingBuyer = useSelector(
    (state) => state.BuyerReducer?.isUpdatingBuyer,
  );

  const provinceList = useSelector(
    (state) => state.CompanyListReducer?.provinceList,
  );

  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );

  const { userDetails } = useSelector((state) => state?.HeaderReducer);

  const buyerTaxStatus = useSelector(
    (state) => state.BuyerReducer?.buyerTaxStatus,
  );

  const [formData, setFormData] = useState(() => ({
    businessName: savedBusinessName,
    contact: savedContactOf(buyer?.contact),
    registrationNo: savedNumber,
    saletax: buyer?.saletax ?? "",
    registrationType: "",
    province: buyer?.province ?? "",
    address: buyer?.address ?? "",
    userId: "",
  }));

  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState(
    isEdit && !savedNumber ? TAB.MANUAL : TAB.CNIC,
  );
  const [atlStatus, setAtlStatus] = useState(savedAtl || DEFAULT_ATL_STATUS);
  // With CNIC: the buyer is looked up by CNIC or by NTN, as picked. Without
  // CNIC: the number field holds an (optional) NTN.
  const [lookupIdType, setLookupIdType] = useState(
    savedNumber && savedNumber.length !== 13 ? "NTN" : "CNIC",
  );
  const idType = tab === TAB.CNIC ? lookupIdType : "NTN";
  // Edit: the statuses saved with the buyer stand for its saved number.
  const numberNow = completeTaxId(formData.registrationNo, idType);
  const savedLookupId = React.useRef(numberNow).current;
  const onSavedNumber =
    isEdit && Boolean(numberNow) && numberNow === savedLookupId;
  const showsSavedSalesTax = onSavedNumber && Boolean(savedSalesTax);
  const salesTaxCheck = showsSavedSalesTax
    ? savedCheck(savedSalesTax)
    : buyerTaxStatus.salesTax;
  // With CNIC: Registered, unless FBR's sales tax check says the number is
  // not registered - then the buyer is saved as Unregistered, keeping its
  // number. Without CNIC: Registered when an NTN is entered, Unregistered when
  // it is left blank.
  const fbrNotRegistered =
    tab === TAB.CNIC && salesTaxCheck.status === "Not Registered";
  const registrationType =
    tab === TAB.CNIC
      ? fbrNotRegistered
        ? "Unregistered"
        : "Registered"
      : formData.registrationNo.trim()
        ? "Registered"
        : "Unregistered";

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "contact") {
      let numericValue = value.replace(/\D/g, "");
      numericValue = numericValue.slice(0, 10);
      let formatted = numericValue;
      if (numericValue.length > 3) {
        formatted = `${numericValue.slice(0, 3)}-${numericValue.slice(3)}`;
      }
      if (numericValue && numericValue[0] !== "3") {
        setErrors((prev) => ({
          ...prev,
          [name]: "Pakistani number must start with 3",
        }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
      return;
    }
    if (name === "registrationNo") {
      let processedValue;
      if (idType === "NTN") {
        const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
        const first = cleaned.slice(0, 1);
        const rest = cleaned.slice(1).replace(/[^0-9]/g, "");
        // 7 characters, plus an optional check digit.
        processedValue = (first + rest).slice(0, 8);
      } else {
        processedValue = value.replace(/\D/g, "").slice(0, 13);
      }
      setErrors((prev) => ({ ...prev, [name]: "" }));
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "businessName",
      tab === TAB.CNIC && "registrationNo",
      "province",
      "address",
      // Edit keeps the user the buyer belongs to.
      !isEdit && userDetails?.role === "admin" && "userId",
    ].filter(Boolean);

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = "This field is required";
      }
    });
    if (formData.contact) {
      const numericValue = formData.contact.replace(/\D/g, "");
      if (numericValue.length !== 10) {
        newErrors.contact = "Contact number must be 10 digits";
      } else if (!numericValue.startsWith("3")) {
        newErrors.contact = "Pakistani number must start with 3";
      }
    }
    if (formData.registrationNo && idType === "NTN") {
      if (!completeTaxId(formData.registrationNo, "NTN")) {
        newErrors.registrationNo =
          "NTN must be 7 digits (optionally with its check digit, XXXXXXX-X), or 1 letter followed by 6 digits";
      }
    }
    if (formData.registrationNo && idType === "CNIC") {
      if (!completeTaxId(formData.registrationNo, "CNIC")) {
        newErrors.registrationNo = "CNIC must be 13 digits (XXXXX-XXXXXXX-X)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    const submitData = {
      ...formData,
      registrationType,
      contact: formData.contact
        ? "92" + formData.contact.replace(/\D/g, "")
        : "",
      // Without an NTN's check digit: FBR's invoices take 7 characters. An
      // unregistered buyer from the With CNIC tab keeps the number it was
      // looked up by.
      registrationNo:
        registrationType === "Unregistered" && tab !== TAB.CNIC
          ? ""
          : completeTaxId(formData?.registrationNo, idType) ||
            formData?.registrationNo,
      saletax: registrationType === "Unregistered" ? "" : formData?.saletax,
      ...(tab === TAB.CNIC && { atlStatus }),
    };

    if (isEdit) {
      const { userId, ...changes } = submitData;
      onSave(changes);
      return;
    }
    // A saved buyer clears the form; the next one starts at Active again.
    const resetForm = (next) => {
      setFormData(next);
      setAtlStatus(DEFAULT_ATL_STATUS);
    };
    dispatch(
      addBuyer(submitData, resetForm, handleVisible, handleDangerVisible),
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getProvince());
      if (!isEdit) dispatch(getUserList("", "", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch, isEdit]);

  // Live tax status: checked as soon as a Registered buyer's number is
  // complete and valid, checked again when it changes to another number, and
  // cleared while it is incomplete or the buyer is not Registered. An NTN's
  // check digit does not change the number checked.
  const isRegistered = registrationType === "Registered";
  // With CNIC the number is always checked - the registration type follows
  // the answer, so it cannot also decide whether to ask.
  const checksNumber = tab === TAB.CNIC || isRegistered;
  const lookupId = checksNumber
    ? completeTaxId(formData.registrationNo, idType)
    : "";
  // The business name last filled in from the Active Taxpayer List.
  const filledName = React.useRef("");
  useEffect(() => {
    if (lookupId && onSavedNumber) {
      // Edit, on the saved number: only a status it has none of is asked for.
      dispatch(clearBuyerTaxStatus());
      if (!savedSalesTax) dispatch(checkBuyerTaxStatus("salesTax", lookupId));
      if (!savedAtl) dispatch(checkBuyerTaxStatus("atl", lookupId));
    } else {
      dispatch(lookupId ? getBuyerTaxStatus(lookupId) : clearBuyerTaxStatus());
    }
    // A name filled in for the previous number is not this one's - but one
    // the user typed is theirs, and stays. Edit: back on the saved number,
    // the saved name comes back. (Read before it is reset: the updater below
    // runs later.)
    const previousFill = filledName.current;
    filledName.current = "";
    setFormData((prev) =>
      previousFill && prev.businessName === previousFill
        ? { ...prev, businessName: onSavedNumber ? savedBusinessName : "" }
        : prev,
    );
  }, [
    dispatch,
    lookupId,
    onSavedNumber,
    savedSalesTax,
    savedAtl,
    savedBusinessName,
  ]);
  // Leaving the form drops whatever is shown or still on its way.
  useEffect(() => () => dispatch(clearBuyerTaxStatus()), [dispatch]);

  // With CNIC: the business name comes from the Active Taxpayer List - the
  // first business it lists for the CNIC, or the person's own name when it
  // lists none. The field stays editable.
  const atlCheck = buyerTaxStatus.atl;
  const taxpayer = atlCheck.taxpayer;
  // The ATL Status follows the list's answer for the number - Active while
  // there is none (no number yet, still looking, or the lookup failed) - and
  // can still be changed by hand after it. Edit: the saved status for the
  // saved number.
  const atlAnswer =
    onSavedNumber && savedAtl ? savedAtl : atlCheck.status;
  useEffect(() => {
    setAtlStatus(
      ATL_STATUSES.includes(atlAnswer) ? atlAnswer : DEFAULT_ATL_STATUS,
    );
  }, [atlAnswer]);
  useEffect(() => {
    // Edit: the saved business name stays while the number is the saved one.
    if (tab !== TAB.CNIC || !taxpayer || onSavedNumber) return;
    const name = (taxpayer.businessNames?.[0] || taxpayer.name || "").trim();
    if (!name) return;
    filledName.current = name;
    setFormData((prev) => ({ ...prev, businessName: name }));
    setErrors((prev) => ({ ...prev, businessName: "" }));
  }, [tab, taxpayer, onSavedNumber]);

  const selectTab = (next) => {
    if (next === tab) return;
    setTab(next);
    // The number belongs to the tab it was typed in.
    setFormData((prev) => ({ ...prev, registrationNo: "", saletax: "" }));
    setErrors((prev) => ({ ...prev, registrationNo: "", saletax: "" }));
  };

  const selectLookupIdType = (next) => {
    if (next === lookupIdType) return;
    setLookupIdType(next);
    // A CNIC is not an NTN: the number typed for one does not carry over.
    setFormData((prev) => ({ ...prev, registrationNo: "" }));
    setErrors((prev) => ({ ...prev, registrationNo: "" }));
  };

  return (
    <div className="w-full min-h-screen flex flex-col lg:flex-row lg:items-start lg:justify-center gap-6 py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="w-full min-w-0 space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          {isEdit ? "Edit Buyer Information" : "Add Buyer Information"}
        </h2>

        {/* TABS */}
        {/* The picked tab is filled with the app's button gradient, like
            every other primary button; buttonColor is a gradient, so it can
            only be a background - not a text or border colour. */}
        <div
          role="tablist"
          aria-label="How to add the buyer"
          className="flex gap-2 p-1.5 rounded-lg bg-gray-100 border border-gray-300"
        >
          {TABS.map((option) => {
            const selected = tab === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => selectTab(option.value)}
                className={`flex-1 px-5 py-2.5 rounded-md font-semibold transition-colors ${
                  selected
                    ? "text-white shadow-md"
                    : "text-gray-600 hover:bg-white hover:text-gray-900"
                }`}
                style={
                  selected ? { background: globalStyle.buttonColor } : undefined
                }
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* GRID */}
        {!isEdit &&
          userDetails?.role === "admin" &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={formData?.userId}
                onChange={handleChange}
                disabled={!userList || userList.length === 0}
                className={`w-full px-4 py-2 border rounded-md ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
              {errors.userId && (
                <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
              )}
            </div>
          ))}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* With CNIC: the number comes first - a CNIC or an NTN, as
              picked - and everything it finds fills in below it. */}
          {tab === TAB.CNIC && (
            <div className="md:col-span-2">
              <div
                role="radiogroup"
                aria-label="Look the buyer up by"
                className="flex gap-6 mb-2"
              >
                {["NTN", "CNIC"].map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer font-medium"
                  >
                    <input
                      type="radio"
                      name="lookupIdType"
                      value={option}
                      checked={lookupIdType === option}
                      onChange={() => selectLookupIdType(option)}
                    />
                    {option}
                    {lookupIdType === option && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                ))}
              </div>
              <input
                aria-label={lookupIdType}
                type="text"
                inputMode={lookupIdType === "CNIC" ? "numeric" : "text"}
                name="registrationNo"
                value={formatTaxId(formData.registrationNo, lookupIdType)}
                onChange={handleChange}
                placeholder={
                  lookupIdType === "CNIC"
                    ? "XXXXX-XXXXXXX-X"
                    : "XXXXXXX or XXXXXXX-X"
                }
                className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                  errors.registrationNo
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              />
              {errors.registrationNo && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.registrationNo}
                </p>
              )}
              {lookupId && (
                <div aria-live="polite" className="text-sm mt-1">
                  {atlCheck.isChecking ? (
                    <span className="flex items-center gap-2 text-gray-600">
                      <CircularProgress size={14} />
                      Looking up this {idType} in the Active Taxpayer List...
                    </span>
                  ) : atlCheck.error ? (
                    <span className="flex flex-wrap items-center gap-2 text-red-600">
                      {atlCheck.error}
                      <button
                        type="button"
                        onClick={() =>
                          dispatch(checkBuyerTaxStatus("atl", lookupId))
                        }
                        className="px-3 py-1 rounded text-white text-sm"
                        style={{
                          background: globalStyle.buttonColor,
                          fontFamily: globalStyle.fontFamilyButton,
                        }}
                      >
                        Retry
                      </button>
                    </span>
                  ) : onSavedNumber ? null : taxpayer ? (
                    <span className="text-green-700">
                      Found on the Active Taxpayer List: {taxpayer.name}.
                      {taxpayer.businessNames?.length > 1 &&
                        ` It lists ${taxpayer.businessNames.length} businesses for this ${idType}; the first was filled in - change it if needed.`}
                    </span>
                  ) : atlCheck.status ? (
                    <span className="text-red-600">
                      This {idType} was not found in the Active Taxpayer List.
                      Enter the business name yourself.
                    </span>
                  ) : null}
                </div>
              )}
              {fbrNotRegistered && (
                <p className="text-sm mt-1 text-amber-700">
                  FBR lists this {idType} as not registered for sales tax, so
                  the buyer will be saved as an Unregistered buyer.
                </p>
              )}
            </div>
          )}
          <InputField
            label="Bussiness Name"
            name="businessName"
            value={formData.businessName}
            error={errors.businessName}
            onChange={handleChange}
            required
          />
          <MobileNumberField
            label="Contact No."
            name="contact"
            value={formData.contact}
            error={errors.contact}
            onChange={handleChange}
          />
          {/* Without CNIC: an NTN makes the buyer Registered; left blank,
              the buyer is saved as Unregistered. */}
          {tab === TAB.MANUAL && (
            <div className="w-full">
              <label htmlFor="buyer-ntn" className="block mb-1 font-medium">
                NTN{" "}
                <span className="text-gray-500 text-sm font-normal">
                  (optional - leave blank for an unregistered buyer)
                </span>
              </label>
              <input
                id="buyer-ntn"
                type="text"
                name="registrationNo"
                value={formatTaxId(formData.registrationNo, "NTN")}
                onChange={handleChange}
                placeholder="XXXXXXX or XXXXXXX-X"
                className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                  errors.registrationNo
                    ? "border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              />
              {errors.registrationNo && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.registrationNo}
                </p>
              )}
            </div>
          )}
          {isRegistered && (
            <InputField
              label="Sale Tax No. (STRN)"
              name="saletax"
              type="number"
              value={formData.saletax}
              error={errors.saletax}
              onChange={handleChange}
            />
          )}
          {tab === TAB.CNIC && (
            <div>
              <label
                htmlFor="searchable-select-atlStatus"
                className="block mb-1 font-medium"
              >
                ATL Status
              </label>
              <SearchableSelect
                name="atlStatus"
                value={atlStatus}
                onChange={(e) => setAtlStatus(e.target.value)}
                className="w-full px-4 py-2 border rounded-md border-gray-300"
              >
                {ATL_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div>
            <label className="block mb-1 font-medium">
              Province<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="province"
              value={formData.province}
              onChange={handleChange}
              disabled={!provinceList || provinceList.length === 0}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.province ? "border-red-500" : "border-gray-300"
              }`}
            >
              {!provinceList || provinceList.length === 0 ? (
                <option value="">No Province Available</option>
              ) : (
                <>
                  <option value="">Select</option>
                  {provinceList.map((value) => (
                    <option
                      key={value.stateProvinceCode}
                      value={value.stateProvinceDesc}
                    >
                      {value.stateProvinceDesc}
                    </option>
                  ))}
                </>
              )}
            </SearchableSelect>
            {errors.province && (
              <p className="text-red-500 text-sm mt-1">{errors.province}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <InputField
              label="Address"
              name="address"
              value={formData.address}
              error={errors.address}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* SUBMIT */}
        {isEdit ? (
          <div className="w-full md:w-1/3 mx-auto flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-2 rounded-md font-bold border border-gray-400 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdatingBuyer}
              className="w-full py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: globalStyle.buttonColor }}
            >
              {isUpdatingBuyer ? "Updating..." : "Update"}
            </button>
          </div>
        ) : (
          <button
            type="submit"
            disabled={isAddingBuyer}
            className="w-full md:w-1/3 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
            style={{ background: globalStyle.buttonColor }}
          >
            {isAddingBuyer ? "Please Wait..." : "Submit"}
          </button>
        )}
      </form>

      {/* Right of the form on large screens, below it on small ones. */}
      {checksNumber && (
        <aside
          aria-label="Buyer tax status"
          className="w-full lg:w-72 lg:shrink-0 lg:sticky lg:top-4 flex flex-col gap-4"
        >
          <TaxStatusBox
            title="Sales Tax Status"
            check={salesTaxCheck}
            idleText={`Enter a complete ${idType} to check.`}
            onRetry={() => dispatch(checkBuyerTaxStatus("salesTax", lookupId))}
            // Asks FBR again for the number entered. Not for a status saved
            // with the buyer (Edit): an unchanged number is not re-checked
            // when it is saved, so a fresh answer would not be kept.
            onRefresh={
              lookupId && !showsSavedSalesTax
                ? () => dispatch(checkBuyerTaxStatus("salesTax", lookupId))
                : undefined
            }
          />
          {/* No ATL Status box: With CNIC shows the list's answer in the
              ATL Status dropdown instead. */}
        </aside>
      )}
    </div>
  );
};

export default AddBuyer;
